import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Divider, Flex, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {noop} from '../../utils/utils'
import {remCalc} from '../../theme/_utils'
import Field from '../field'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {MESSAGE_PROPTYPE} from '../../utils/locale'
import useAddressFields from '../forms/use-address-fields'
import VatValidator from '../vat-validator'
import {useCountryLabels} from './use-country-labels'
import LoqateFields from './loqate-fields'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {isPecualiarAddressFormCountry} from '../../utils/peculiar-address-form'

const defaultFormTitleAriaLabel = defineMessage({
    defaultMessage: 'Address Form',
    id: 'use_address_fields.label.address_form',
})

const AddressFieldsWithAutocomplete = ({
    form,
    defaultShowAutocomplete = true,
    prefix = '',
    formTitleAriaLabel = defaultFormTitleAriaLabel,
    isBillingAddress = false,
    addressType = 'private',
    variant = 'default',
    isContactUs = false,
    contactUsConfigFields = {},
    action = noop,
    setAutocompleteObj,
}) => {
    const {data: customer} = useCurrentCustomer()
    const styles = useMultiStyleConfig('AddressFields', {variant})
    const [showAutocomplete, setShowAutocomplete] = useState(defaultShowAutocomplete)

    const {formatMessage} = useIntl()

    const {getCountries} = useCountryLabels()

    const [countriesArray, setCountriesArray] = useState([])

    const selectedCountryWatcher = form.watch(`countryCode`)
    const selectedCountry =
        typeof selectedCountryWatcher === 'string'
            ? selectedCountryWatcher
            : selectedCountryWatcher?.value

    // reset stateCode when country changes, only for billing address forms
    const countryCodeRef = useRef(selectedCountry)
    useEffect(() => {
        if (isBillingAddress && countryCodeRef.current !== selectedCountry) {
            form.setValue('stateCode', '')
            countryCodeRef.current = selectedCountry
        }
    }, [selectedCountry])

    const fields = useAddressFields({
        form,
        prefix,
        countries: countriesArray,
        isContactUs: isContactUs,
    })

    useEffect(() => {
        setCountriesArray(getCountries())

        const values = form.getValues()
        if (values.address1) {
            setShowAutocomplete(false)
        }
    }, [])

    const watcherStateCode = form.watch('stateCode')

    const province = useMemo(() => {
        const stateCode =
            typeof watcherStateCode === 'string' ? watcherStateCode : watcherStateCode?.value

        return fields.stateCode.options?.find((province) => province.value === stateCode)
    }, [])

    const addressFormRef = useRef()

    useEffect(() => {
        if (province) form.setValue('stateCode', province)
        // Focus on the form when the component mounts for accessibility
        addressFormRef?.current?.focus()
    }, [])
    const {locale} = useMultiSite()
    const isPeculiarAddressFormCountry = isPecualiarAddressFormCountry(locale)
    return isPeculiarAddressFormCountry ? (
        <Stack
            {...styles.container}
            aria-label={formatMessage(formTitleAriaLabel)}
            tabIndex="0"
            ref={addressFormRef}
        >
            {addressType === 'business' && <VatValidator form={form} fields={fields} />}

            {!isContactUs && (
                <>
                    <Flex {...styles.gaps} {...(!customer.isGuest ? styles.divider : {})}>
                        <Field {...fields.lastName} />
                        <Field {...fields.firstName} />
                    </Flex>
                </>
            )}
            <LoqateFields
                form={form}
                defaultShowAutocomplete={showAutocomplete}
                prefix={prefix}
                isBillingAddress={isBillingAddress}
                addressType={addressType}
                variant={variant}
                action={action}
                isContactUs={isContactUs}
                contactUsConfigFields={contactUsConfigFields}
            />
            <Stack gap={remCalc(8)}>
                {!isContactUs && (
                    <Flex {...styles.gaps}>
                        <Field {...fields.phone} />
                        <Box w="100%" display={{base: 'none', md: 'block'}} />
                    </Flex>
                )}
                {!isContactUs && (
                    <Text {...styles.deliveryNoteText}>
                        <FormattedMessage
                            defaultMessage="We'll use it for delivery purposes"
                            id="address_fields.label.delivery_purposes_note"
                        />
                    </Text>
                )}
            </Stack>
            {!isContactUs && customer.isRegistered && (
                <>
                    <Field {...fields.preferred} variant="row" />
                </>
            )}
        </Stack>
    ) : (
        <Stack
            {...styles.container}
            aria-label={formatMessage(formTitleAriaLabel)}
            ref={addressFormRef}
        >
            {addressType === 'business' && <VatValidator form={form} fields={fields} />}

            {!isContactUs && (
                <>
                    <Flex {...styles.gaps} zIndex={10}>
                        <Field {...fields.title} />
                        <Box w="100%" display={{base: 'none', md: 'block'}} />
                    </Flex>
                    <Flex {...styles.gaps}>
                        <Field {...fields.firstName} />
                        <Field {...fields.lastName} />
                    </Flex>
                    {!customer.isGuest && <Divider />}
                </>
            )}

            <LoqateFields
                form={form}
                defaultShowAutocomplete={showAutocomplete}
                prefix={prefix}
                isBillingAddress={isBillingAddress}
                addressType={addressType}
                variant={variant}
                action={action}
                isContactUs={isContactUs}
                contactUsConfigFields={contactUsConfigFields}
                setAutocompleteObj={setAutocompleteObj}
            />
            {/* Unavailable Lookup Alert
            {!isLoqateFlag && showAutocomplete && !autocompleteAvailable && (
                <Text {...styles.unavailable}>
                    <FormattedMessage
                        defaultMessage="Address lookup service is unavailable. Please enter your address manually."
                        id="address_fields.error.lookup_service_unavailable"
                    />
                </Text>
            )}

            {(!showAutocomplete || !autocompleteAvailable || !isLoqateFlag) && (
                <>
                    {isBillingAddress && addressType === 'business' && (
                        <Flex
                            {...{
                                textStyle: 'body-xsmall',
                                gap: remCalc(8),
                                paddingTop: remCalc(16),
                                borderTop: 'small',
                                borderStyle: 'solid',
                                borderColor: 'border.light',
                                cursor: 'pointer',
                                alignItems: 'center',
                            }}
                            onClick={() => action()}
                        >
                            <AdviseIcon />
                            <Text>
                                <FormattedMessage
                                    defaultMessage="Click to fill in the fields below with your shipping address data."
                                    id="address_fields.label.click_to_fill_address"
                                />
                            </Text>
                        </Flex>
                    )}

                    <Field {...fields.address1} />
                    {showAddress2 || watcheraddress2 ? (
                        <Field {...fields.address2} />
                    ) : (
                        <Button
                            {...styles.backToLookupBtn}
                            data-testid="show-address2-btn"
                            onClick={() => setShowAddress2(true)}
                        >
                            <FormattedMessage
                                defaultMessage="Add more details"
                                id="address_fields.label.add_more_details"
                            />
                        </Button>
                    )}

                    <Flex {...styles.gaps}>
                        <Field {...fields.city} />
                        <Field {...fields.stateCode} isSearchable={true} />
                    </Flex>
                    <Flex {...styles.gaps}>
                        <Field {...fields.postalCode} />
                        <Field {...fields.countryCode} />
                    </Flex>

                    <Flex>
                        {isLoqateFlag && autocompleteAvailable && (
                            <Button
                                {...styles.backToLookupBtn}
                                data-testid="lookup-btn"
                                onClick={() => refreshForm('manual')}
                            >
                                <FormattedMessage
                                    defaultMessage="Back to address lookup"
                                    id="address_fields.label.backToAddressLookup"
                                />
                            </Button>
                        )}
                    </Flex>
                </>
            )}
            {isLoqateFlag && showAutocomplete && autocompleteAvailable && (
                <Flex zIndex={2}>
                    <Field
                        {...fields.addressLookup}
                        setAutocompleteAvailable={setAutocompleteAvailable}
                        toggleAutocomplete={() => refreshForm('lookup')}
                        onAddressUpdate={onAddressUpdate}
                        elementsCount={isMobile ? 3 : 5}
                    />
                </Flex>
            )} */}

            {!isContactUs && (
                <Stack gap={remCalc(8)}>
                    <Flex {...styles.gaps}>
                        <Field {...fields.phone} />
                        <Box w="100%" display={{base: 'none', md: 'block'}} />
                    </Flex>
                    <Text {...styles.deliveryNoteText}>
                        <FormattedMessage
                            defaultMessage="We'll use it for delivery purposes"
                            id="address_fields.label.delivery_purposes_note"
                        />
                    </Text>
                </Stack>
            )}
            {!isContactUs && customer.isRegistered && (
                <>
                    <Field {...fields.preferred} variant="row" />
                </>
            )}
        </Stack>
    )
}

AddressFieldsWithAutocomplete.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,
    /** Optional prefix for field names */
    prefix: PropTypes.string,
    edit: PropTypes.object,
    fromEdit: PropTypes.bool,
    /** Default autocomplete visibility status */
    defaultShowAutocomplete: PropTypes.bool,
    enableEditing: PropTypes.bool,

    /** Optional aria label to use for the address form */
    formTitleAriaLabel: MESSAGE_PROPTYPE,

    /** Optional flag to indication if an address is a billing address */
    isBillingAddress: PropTypes.bool,
    addressType: PropTypes.oneOf(['private', 'business']),
    action: PropTypes.func,
    variant: PropTypes.string,
    isContactUs: PropTypes.bool,
    contactUsConfigFields: PropTypes.object,
    setAutocompleteObj: PropTypes.func,
}

export default AddressFieldsWithAutocomplete
